import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { LoadingProgress } from '../../core/LoadingManager';

@customElement('himum-loading-overlay')
export class HimumLoadingOverlay extends LitElement {
  @state() private progress: LoadingProgress = {
    loaded: 0,
    total: 0,
    percent: 0,
    currentItem: '',
  };

  @state() private visible = true;

  private boundOnProgress = this.onProgress.bind(this);
  private boundOnComplete = this.onComplete.bind(this);

  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000;
      transition: opacity 0.3s ease-out;
    }

    :host([hidden]) {
      display: none;
    }

    .container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      width: 280px;
    }

    .title {
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
      font-size: 24px;
      font-weight: 700;
      color: #fff;
      letter-spacing: 0.05em;
    }

    .progress-bar {
      width: 100%;
      height: 8px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: #fff;
      border-radius: 4px;
      transition: width 0.2s ease-out;
    }

    .status {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
      text-align: center;
      min-height: 1.2em;
    }

    .percent {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('himum-loading-progress', this.boundOnProgress as EventListener);
    window.addEventListener('himum-loading-complete', this.boundOnComplete as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('himum-loading-progress', this.boundOnProgress as EventListener);
    window.removeEventListener('himum-loading-complete', this.boundOnComplete as EventListener);
  }

  private onProgress(e: CustomEvent<LoadingProgress>): void {
    this.progress = e.detail;
  }

  private onComplete(): void {
    this.visible = false;
    // Remove from DOM after fade animation
    setTimeout(() => {
      this.remove();
    }, 300);
  }

  render() {
    if (!this.visible) {
      return html``;
    }

    const itemText = this.progress.currentItem 
      ? `Loading ${this.progress.currentItem}...`
      : 'Initializing...';

    return html`
      <div class="container">
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${this.progress.percent}%"></div>
        </div>
        <div class="percent">${this.progress.percent}%</div>
        <div class="status">${itemText}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'himum-loading-overlay': HimumLoadingOverlay;
  }
}
